'use client';
import { DynamicIcon } from '@curology/ui-components-web/react/Icon/DynamicIcon';
import { AgencyCheckmarkIcon } from '@curology/ui-components-web/react/Icon/icons/AgencyCheckmarkIcon';
import { CheckmarkIcon } from '@curology/ui-components-web/react/Icon/icons/CheckmarkIcon';
import { Typography } from '@curology/ui-components-web/react/Typography';
import clsx from 'clsx';
import React from 'react';
import { type ItemValue, type QuestionCheckboxModel } from 'survey-core';

import { useFlowContext } from '@/components/flow/hooks/useFlowContext';
import { type Brand } from '@/consts/brands';

const getBrandStyles = (brand: Brand) => {
  switch (brand) {
    case 'agency':
      return {
        checkmarkIcon: <AgencyCheckmarkIcon size="sm" />,
        selectedClasses:
          'cur-bg-surface-highlight cur-ring-4 cur-ring-surface-secondary',
        unselectedClasses:
          'cur-bg-surface-primary cur-ring-2 cur-ring-surface-disabled',
      };
    case 'curology':
    default:
      return {
        checkmarkIcon: (
          <CheckmarkIcon className="cur-text-surface-brand-accent" size="sm" />
        ),
        selectedClasses:
          'cur-bg-surface-highlight cur-ring-4 cur-ring-surface-brand',
        unselectedClasses:
          'cur-bg-surface-neutral cur-ring-4 cur-ring-transparent',
      };
  }
};

const CheckboxItem = ({
  question,
  item,
}: {
  question: QuestionCheckboxModel;
  item: ItemValue;
}) => {
  const { brand } = useFlowContext();

  const brandStyles = getBrandStyles(brand);

  const handleOnChange = () => {
    question.choices.forEach((checkboxItem: ItemValue) => {
      if (
        item.clearOtherValues
          ? !checkboxItem.clearOtherValues
          : checkboxItem.clearOtherValues
      ) {
        question.clickItemHandler(checkboxItem, false);
      }
    });
    question.clickItemHandler(item, !item.selected);
  };

  return (
    <div>
      <div role="presentation" className="cur-text-primary">
        <input
          role="option"
          aria-selected={question.isItemSelected(item)}
          className="cur-peer cur-sr-only"
          id={question.getItemId(item)}
          type="checkbox"
          name={question.questionName}
          defaultChecked={question.isItemSelected(item)}
          value={item.value}
          onClick={handleOnChange}
          tabIndex={0}
        />
        <label
          htmlFor={question.getItemId(item)}
          className={clsx(
            'cur-flex cur-relative cur-items-center cur-py-4 cur-pl-4 cur-pr-6 cur-cursor-pointer peer-focus-visible:cur-ring-offset-border-brand-selected peer-focus-visible:cur-ring-offset-2',
            question.isItemSelected(item)
              ? brandStyles.selectedClasses
              : brandStyles.unselectedClasses,
          )}
        >
          {item.inputIcon && (
            <DynamicIcon
              name={item.inputIcon}
              size="lg"
              className="cur-mr-3 cur-flex-shrink-0"
            />
          )}
          <div>
            <Typography size="body" weight="bold">
              {item.text}
            </Typography>
            {item.description && (
              <Typography size="meta">{item.description}</Typography>
            )}
          </div>
          <div className="cur-w-4 cur-flex cur-right-0 cur-mr-2 cur-ml-auto">
            {question.isItemSelected(item) && brandStyles.checkmarkIcon}
          </div>
        </label>
      </div>
      {question.isItemSelected(item) && item.selectionText && (
        <Typography size="meta" className="cur-pt-2 cur-text-highlight">
          {item.selectionText}
        </Typography>
      )}
    </div>
  );
};

export default CheckboxItem;
